import { useRef, useState } from "react";

export default function FloorPlanViewer() {
  const containerRef = useRef(null);

  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const isDragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  const MIN_ZOOM = 1;
  const MAX_ZOOM = 3;

  const BOUND_FACTOR = 250;
  const ELASTICITY = 0.35;

  // 🔍 Zoom
  const zoomIn = () =>
    setScale((p) => Math.min(p + 0.2, MAX_ZOOM));

  const zoomOut = () =>
    setScale((p) => Math.max(p - 0.2, MIN_ZOOM));

  const handleWheel = (e) => {
    e.preventDefault();

    setScale((prev) =>
      e.deltaY < 0
        ? Math.min(prev + 0.1, MAX_ZOOM)
        : Math.max(prev - 0.1, MIN_ZOOM)
    );
  };

  // 🖱️ POINTER DOWN (start drag)
  const handlePointerDown = (e) => {
    isDragging.current = true;

    lastPos.current = {
      x: e.clientX,
      y: e.clientY,
    };

    // 🔥 important: lock pointer to element
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  // 🖱️ POINTER MOVE (smooth drag)
  const handlePointerMove = (e) => {
    if (!isDragging.current) return;

    const dx = e.clientX - lastPos.current.x;
    const dy = e.clientY - lastPos.current.y;

    lastPos.current = {
      x: e.clientX,
      y: e.clientY,
    };

    setPosition((prev) => {
      const rawX = prev.x + dx;
      const rawY = prev.y + dy;

      const bound = (scale - 1) * BOUND_FACTOR;

      let x = rawX;
      let y = rawY;

      // 🧲 elastic X
      if (rawX > bound)
        x = bound + (rawX - bound) * ELASTICITY;
      if (rawX < -bound)
        x = -bound + (rawX + bound) * ELASTICITY;

      // 🧲 elastic Y
      if (rawY > bound)
        y = bound + (rawY - bound) * ELASTICITY;
      if (rawY < -bound)
        y = -bound + (rawY + bound) * ELASTICITY;

      return { x, y };
    });
  };

  // 🖱️ POINTER UP (end drag)
  const handlePointerUp = () => {
    isDragging.current = false;

    const bound = (scale - 1) * BOUND_FACTOR;

    setPosition((prev) => ({
      x: Math.max(-bound, Math.min(bound, prev.x)),
      y: Math.max(-bound, Math.min(bound, prev.y)),
    }));
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100vh",
        background: "#000",
        overflow: "hidden",
        position: "relative",
        touchAction: "none", // 🔥 important for mobile + smooth drag
      }}
      onWheel={handleWheel}
    >
      {/* 🗺️ Image */}
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          cursor: isDragging.current
            ? "grabbing"
            : "grab",
        }}
      >
        <img
          src="/images/FloorPlan.jpg"
          alt="Floor Plan"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          style={{
            transform: `
              translate(${position.x}px, ${position.y}px)
              scale(${scale})
            `,
            transition: isDragging.current
              ? "none"
              : "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1)",
            maxWidth: "100%",
            maxHeight: "100%",
            userSelect: "none",
            pointerEvents: "all",
          }}
        />
      </div>

      {/* 🎛️ Controls */}
      <div
        style={{
          position: "absolute",
          bottom: 20,
          right: 20,
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <button onClick={zoomIn} style={btnStyle}>
          +
        </button>
        <button onClick={zoomOut} style={btnStyle}>
          −
        </button>
      </div>
    </div>
  );
}

const btnStyle = {
  width: 40,
  height: 40,
  fontSize: 20,
  borderRadius: 8,
  border: "1px solid #fff",
  background: "rgba(0,0,0,0.6)",
  color: "#fff",
  cursor: "pointer",
};